/**
 * ContratsCompare — Comparer 2 contrats côte à côte.
 * L'IA identifie les différences clés (prix, préavis, indexation,
 * reconduction, risques) + fait une recommandation.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search, GitCompare, Loader2, TrendingUp, TrendingDown, Minus,
  ShieldCheck, ShieldAlert, ArrowRight, Trophy, Info, Sparkles, X,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmtEUR = (v) => v == null ? "—" : new Intl.NumberFormat("fr-FR",
  { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(v);
const fmtPct = (v) => v == null ? "—" : `${v > 0 ? "+" : ""}${v.toFixed(1)}%`;

function ContractPicker({ label, selected, onSelect, exclude }) {
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [open, setOpen] = useState(false);

  const search = async (query) => {
    try {
      const { data } = await api.get("/contracts/compare/candidates",
        { params: { q: query || "", limit: 30 } });
      setCandidates((data.candidates || []).filter(c => c.id !== exclude));
    } catch (e) { /* silent */ }
  };

  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">
        {label}
      </div>
      {selected ? (
        <Card className="p-3 border-blue-200 bg-blue-50/40">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{selected.title}</div>
              <div className="text-[11px] text-slate-500 truncate">
                {selected.party_name} · {selected.contract_type || "—"} · {selected.reference || "sans réf."}
              </div>
              <div className="text-[11px] text-slate-500 mt-1">
                {fmtEUR(selected.amount_ht)} · Risque {selected.risk_score || 0}
              </div>
            </div>
            <button onClick={() => onSelect(null)} className="text-slate-400 hover:text-red-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        </Card>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input value={q}
                  onChange={(e) => { setQ(e.target.value); setOpen(true); search(e.target.value); }}
                  onFocus={() => { setOpen(true); if (!candidates.length) search(""); }}
                  placeholder="Rechercher un contrat…" className="pl-9" />
          {open && candidates.length > 0 && (
            <div className="absolute z-10 mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto">
              {candidates.map((c) => (
                <button key={c.id} onClick={() => { onSelect(c); setOpen(false); setQ(""); }}
                  className="w-full text-left p-2 hover:bg-blue-50 border-b border-slate-100 last:border-0">
                  <div className="text-xs font-medium truncate">{c.title || c.party_name}</div>
                  <div className="text-[10px] text-slate-500 truncate">
                    {c.party_name} · {c.contract_type || "—"} · {fmtEUR(c.amount_ht)}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Winner({ side, size = "sm" }) {
  if (!side || side === "equal") {
    return <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-[10px]">Égalité</Badge>;
  }
  return (
    <Badge className={cn(size === "lg" ? "text-xs" : "text-[10px]",
      side === "A" ? "bg-blue-100 text-blue-700 border-blue-300"
                    : "bg-emerald-100 text-emerald-700 border-emerald-300")}>
      <Trophy className="h-3 w-3 mr-1" /> {side} gagne
    </Badge>
  );
}

export default function ContratsCompare() {
  const navigate = useNavigate();
  const [contractA, setContractA] = useState(null);
  const [contractB, setContractB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const compare = async () => {
    if (!contractA || !contractB) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/contracts/compare",
        { contract_a: contractA.id, contract_b: contractB.id });
      setResult(data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const cmp = result?.comparison;

  return (
    <div className="space-y-5" data-testid="contracts-compare-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Contrats</div>
        <h1 className="mt-1 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          Comparer 2 contrats
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Utile pour trancher entre un ancien et un nouveau contrat, ou
          entre 2 offres fournisseurs. L'IA met en évidence les deltas
          financiers, contractuels et de risque.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-4 items-start">
        <ContractPicker label="Contrat A" selected={contractA}
                          onSelect={setContractA} exclude={contractB?.id} />
        <div className="hidden md:flex items-center justify-center h-32">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
            <GitCompare className="h-5 w-5 text-white" />
          </div>
        </div>
        <ContractPicker label="Contrat B" selected={contractB}
                          onSelect={setContractB} exclude={contractA?.id} />
      </div>

      <div className="flex justify-center">
        <Button onClick={compare} disabled={!contractA || !contractB || busy}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-11 px-8"
                data-testid="compare-run">
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                : <Sparkles className="h-4 w-4 mr-1.5" />}
          Comparer par IA
        </Button>
      </div>

      {cmp && (
        <div className="space-y-4">
          {/* Executive summary + winner */}
          <Card className="p-5 border-purple-200 bg-gradient-to-br from-purple-50/40 to-pink-50/40">
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="text-[11px] font-medium uppercase tracking-wide text-purple-700 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> Verdict IA
              </div>
              <Winner side={cmp.recommendation_winner} size="lg" />
            </div>
            <p className="text-sm text-slate-800 leading-relaxed">
              {cmp.executive_summary}
            </p>
            {cmp.recommendation && (
              <div className="mt-3 pt-3 border-t border-purple-200/60">
                <div className="text-[10px] uppercase tracking-widest text-purple-700 font-medium mb-1">Recommandation</div>
                <p className="text-xs text-slate-700">{cmp.recommendation}</p>
              </div>
            )}
          </Card>

          {/* Deltas grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Financial */}
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Financier</div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-blue-700 font-medium">A</span>
                <span className="font-mono">{fmtEUR(cmp.financial_delta?.amount_ht_a)}</span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-emerald-700 font-medium">B</span>
                <span className="font-mono">{fmtEUR(cmp.financial_delta?.amount_ht_b)}</span>
              </div>
              <div className="border-t pt-2 flex items-center justify-between">
                <div className="text-[11px] text-slate-500">
                  Δ/an: {fmtEUR(cmp.financial_delta?.annual_diff_ht)} · {fmtPct(cmp.financial_delta?.diff_pct)}
                </div>
                <Winner side={cmp.financial_delta?.winner} />
              </div>
            </Card>

            {/* Duration */}
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Durée</div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-blue-700 font-medium">A</span>
                <span className="font-mono">{cmp.duration_delta?.months_a ?? "—"} mois</span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-emerald-700 font-medium">B</span>
                <span className="font-mono">{cmp.duration_delta?.months_b ?? "—"} mois</span>
              </div>
              <div className="text-[11px] text-slate-500 border-t pt-2">{cmp.duration_delta?.note}</div>
            </Card>

            {/* Notice */}
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Préavis</div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-blue-700 font-medium">A</span>
                <span className="font-mono">{cmp.notice_delta?.days_a ?? "—"} j</span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-emerald-700 font-medium">B</span>
                <span className="font-mono">{cmp.notice_delta?.days_b ?? "—"} j</span>
              </div>
              <div className="border-t pt-2 flex justify-end">
                <Winner side={cmp.notice_delta?.winner} />
              </div>
            </Card>

            {/* Renewal */}
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Reconduction</div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-blue-700 font-medium">A</span>
                <span>{cmp.renewal_delta?.tacit_a ? "Tacite" : "Non tacite"}</span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-emerald-700 font-medium">B</span>
                <span>{cmp.renewal_delta?.tacit_b ? "Tacite" : "Non tacite"}</span>
              </div>
              <div className="text-[11px] text-slate-500 border-t pt-2">{cmp.renewal_delta?.note}</div>
            </Card>

            {/* Indexation */}
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Indexation</div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-blue-700 font-medium">A</span>
                <span className="font-mono">{cmp.indexation_delta?.index_a || "—"}</span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-emerald-700 font-medium">B</span>
                <span className="font-mono">{cmp.indexation_delta?.index_b || "—"}</span>
              </div>
              <div className="text-[11px] text-slate-500 border-t pt-2">{cmp.indexation_delta?.note}</div>
            </Card>

            {/* Risk */}
            <Card className="p-4">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Risque légal</div>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-blue-700 font-medium">A</span>
                <span className="font-mono">{cmp.risk_delta?.score_a}/100</span>
              </div>
              <div className="flex items-center justify-between text-xs mb-2">
                <span className="text-emerald-700 font-medium">B</span>
                <span className="font-mono">{cmp.risk_delta?.score_b}/100</span>
              </div>
              <div className="border-t pt-2 flex items-center justify-between">
                <div className="text-[10px] text-slate-500">
                  {(cmp.risk_delta?.unique_risks_a?.length || 0)} unique A ·
                  {" "}{(cmp.risk_delta?.unique_risks_b?.length || 0)} unique B
                </div>
                <Winner side={cmp.risk_delta?.safer} />
              </div>
            </Card>
          </div>

          {/* Clause differences */}
          {cmp.clause_differences?.length > 0 && (
            <Card className="p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <GitCompare className="h-4 w-4 text-purple-600" />
                Différences clés par clause
              </h3>
              <div className="space-y-2">
                {cmp.clause_differences.map((d, i) => (
                  <div key={i} className={cn("rounded-lg border p-3",
                    d.impact === "favorable_a" ? "border-blue-200 bg-blue-50/30"
                    : d.impact === "favorable_b" ? "border-emerald-200 bg-emerald-50/30"
                    : "border-slate-200 bg-slate-50/30")}
                       data-testid={`clause-diff-${i}`}>
                    <div className="text-xs font-medium text-slate-900 mb-1.5 flex items-center justify-between">
                      <span>{d.topic}</span>
                      <Badge className={cn("text-[10px]",
                        d.impact === "favorable_a" ? "bg-blue-100 text-blue-700"
                        : d.impact === "favorable_b" ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-600")}>
                        {d.impact === "favorable_a" ? "Favorable A"
                          : d.impact === "favorable_b" ? "Favorable B" : "Neutre"}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-[11px]">
                      <div className="border-l-2 border-blue-300 pl-2">
                        <div className="text-blue-700 font-medium uppercase text-[9px] mb-0.5">A</div>
                        <div className="text-slate-700">{d.in_a || "—"}</div>
                      </div>
                      <div className="border-l-2 border-emerald-300 pl-2">
                        <div className="text-emerald-700 font-medium uppercase text-[9px] mb-0.5">B</div>
                        <div className="text-slate-700">{d.in_b || "—"}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={async () => {
              try {
                const r = await api.post("/contracts/compare/export-docx",
                  { contract_a: contractA.id, contract_b: contractB.id },
                  { responseType: "blob", timeout: 90000 });
                const blob = new Blob([r.data], {
                  type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                });
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Comparaison - ${contractA.title || "A"} vs ${contractB.title || "B"}.docx`;
                a.click(); URL.revokeObjectURL(url);
                toast.success("DOCX différentielle téléchargé");
              } catch (e) { toast.error(apiError(e)); }
            }}
              disabled={!contractA || !contractB}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="compare-export-docx">
              <ArrowRight className="h-3 w-3 mr-1 rotate-90" /> Exporter DOCX différentielle
            </Button>
            <Button variant="outline" onClick={() => navigate(`/app/contracts/${result.contract_a.id}`)}>
              Ouvrir A <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
            <Button variant="outline" onClick={() => navigate(`/app/contracts/${result.contract_b.id}`)}>
              Ouvrir B <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
